import React from 'react';
import { Box, Container, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ProductCard from './Product/ProductCard';
import { ProductService } from '../services/productService';

export type PageCardStripSectionConfig = {
  enabled?: boolean;
  heading?: string;
  text?: string;
  productIds?: string[];
  perRow?: number;
  variant?: 'default' | 'compact';
  showSeller?: boolean;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const PageCardStripSection: React.FC<{ config?: PageCardStripSectionConfig | null }> = ({ config }) => {
  const cfg: PageCardStripSectionConfig = (config && typeof config === 'object') ? config : {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const heading = typeof cfg.heading === 'string' ? cfg.heading : '';
  const text = typeof cfg.text === 'string' ? cfg.text : '';
  const productIds = Array.isArray(cfg.productIds) ? cfg.productIds.map((x) => String(x || '').trim()).filter(Boolean) : [];
  const perRow = clamp(cfg.perRow, 1, 6) || 3;
  const variant: 'default' | 'compact' = cfg.variant === 'compact' ? 'compact' : 'default';
  const showSeller = cfg.showSeller !== false;

  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<any[]>([]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (cfg.enabled === false) {
        setItems([]);
        return;
      }
      if (productIds.length === 0) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const rows = await ProductService.getProductsWithSellersByIds(productIds);
        if (!alive) return;
        setItems(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error('PageCardStripSection load failed', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cfg.enabled, productIds.join('|')]);

  const handleViewDetails = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  const showEmpty = !loading && items.length === 0;

  if (cfg.enabled === false) return null;

  return (
    <Box sx={{ py: { xs: 5, md: 7 } }}>
      <Container maxWidth="lg">
        {(heading || text) ? (
          <Box sx={{ mb: 2.25 }}>
            {heading ? (
              <Typography variant="h5" sx={{ fontWeight: 950, letterSpacing: -0.25, mb: text ? 0.5 : 0 }}>
                {heading}
              </Typography>
            ) : null}
            {text ? (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                {text}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {showEmpty ? (
          <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
              Keine Produkte gesetzt.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: `repeat(${Math.min(2, perRow)}, minmax(0, 1fr))`,
                md: `repeat(${perRow}, minmax(0, 1fr))`,
              },
              gap: { xs: 2, md: 2.25 },
              alignItems: 'stretch',
            }}
          >
            {(loading && items.length === 0) ? (
              <Box sx={{ gridColumn: '1 / -1', p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Lädt...
                </Typography>
              </Box>
            ) : (
              items.map((p: any) => (
                <Box key={String(p?.id || Math.random())} sx={{ minWidth: 0 }}>
                  <ProductCard
                    product={p}
                    variant={variant}
                    showSeller={showSeller}
                    onViewDetails={(id) => handleViewDetails(id)}
                    isFavorite={false}
                    isOwnProduct={false}
                    onAddToCart={() => {}}
                    onToggleFavorite={() => {}}
                    onShare={() => {}}
                    navigationState={isMobile ? undefined : undefined}
                  />
                </Box>
              ))
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PageCardStripSection;
